import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import POINTS_MANIFEST from '../../assets/json/points-manifest.json';

/**
 * Nummern, die in der Punktarten-Liste mehr als einmal vorkommen (leer = eindeutig).
 * Single-Source fuer die Dubletten-Markierung im Editor UND den Speicher-Block der
 * Listenverwaltung: `bp.type` referenziert die Nummer als stabilen Schluessel —
 * doppelte Nummern wuerden den Punktart-Lookup mehrdeutig machen.
 */
export function duplicatePunktartNummern(data: any[] | undefined | null): number[] {
  const counts = new Map<number, number>();
  for (const r of data ?? []) {
    const n = Number(r?.nummer);
    if (Number.isFinite(n)) counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  return [...counts.entries()].filter(([, c]) => c > 1).map(([n]) => n);
}

/**
 * Dedizierter Editor fuer die Punktarten-Liste (DB `bpweb-listen`).
 *
 * Spalten: Nummer (= bp.type, stabiler Schluessel), Beschreibung, Kategorie
 * (Legenden-Gruppe) und das Symbol. Das Symbol kann entweder aus den
 * mitgelieferten SVGs ausgewaehlt (Spalte `svg`) ODER als eigene Datei
 * hochgeladen werden (Spalte `svgData` als Data-URL, hat Vorrang). Der Editor
 * mutiert das uebergebene `data`-Array IN PLACE und meldet Aenderungen via
 * `changed`; gespeichert wird ueber den „Speichern"-Button der Listenverwaltung.
 */
@Component({
  standalone: false,
  selector: 'app-punktarten-editor',
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
    .pa-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; }
    .pa-symbol { width: 28px; height: 28px; object-fit: contain; background: #fff; border: 1px solid #dee2e6; border-radius: 3px; }
    .pa-num { max-width: 90px; }
    .pa-svgsel { min-width: 180px; }
  `],
  template: `
    <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
      <div class="input-group input-group-sm" style="max-width: 320px;">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input type="text" class="form-control" [(ngModel)]="searchTerm"
               (ngModelChange)="recompute()" placeholder="Suchen (Nummer, Beschreibung, Kategorie)…">
      </div>
      @if (!readonly) {
        <button type="button" class="btn btn-outline-success btn-sm" (click)="addRow()">
          <i class="bi bi-plus-lg"></i> Punktart
        </button>
      }
      <span class="text-muted small ms-auto">{{ filteredCount }} Einträge</span>
    </div>

    @if (duplicates.length) {
      <div class="alert alert-danger py-1 px-2 mb-2 small d-flex align-items-center gap-2">
        <i class="bi bi-exclamation-triangle"></i>
        <span>Doppelte Nummer(n): <strong>{{ duplicates.join(', ') }}</strong>. Nummern müssen eindeutig sein – Speichern ist erst nach Korrektur möglich.</span>
      </div>
    }

    <div class="table-responsive pa-scroll">
      <table class="table table-sm table-hover align-middle mb-0">
        <thead class="sticky-top bg-light">
          <tr>
            <th style="width: 90px;">Nummer</th>
            <th>Beschreibung</th>
            <th>Kategorie</th>
            <th style="width: 320px;">Symbol</th>
            @if (!readonly) { <th class="text-end" style="width: 1%;"></th> }
          </tr>
        </thead>
        <tbody>
          @for (row of view; track row.index) {
            <tr>
              <td>
                <input type="number" class="form-control form-control-sm pa-num"
                       [ngModel]="row.item.nummer" (ngModelChange)="setNummer(row.item, $event)"
                       [readonly]="!canEditNummer(row.item)"
                       [class.is-invalid]="isDuplicateNummer(row.item)"
                       [title]="canEditNummer(row.item) ? 'Nummer der neuen Punktart (muss eindeutig sein)' : 'Die Nummer ist der feste Schlüssel bestehender Einträge und kann nicht geändert werden – bestehende Punkte verweisen darauf.'">
              </td>
              <td>
                <input type="text" class="form-control form-control-sm"
                       [ngModel]="row.item.beschreibung" (ngModelChange)="setField(row.item, 'beschreibung', $event)"
                       [readonly]="readonly">
              </td>
              <td>
                <input type="text" class="form-control form-control-sm" list="pa-kategorien"
                       [ngModel]="row.item.kategorie" (ngModelChange)="setField(row.item, 'kategorie', $event)"
                       [readonly]="readonly">
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <img class="pa-symbol" [src]="iconUrl(row.item)" alt=""
                       onerror="this.style.visibility='hidden'">
                  @if (!readonly) {
                    <select class="form-select form-select-sm pa-svgsel"
                            [ngModel]="row.item.svgData ? '' : (row.item.svg || '')"
                            (ngModelChange)="selectSvg(row.item, $event)"
                            [disabled]="!!row.item.svgData"
                            title="Mitgeliefertes Symbol wählen">
                      <option value="">– Symbol wählen –</option>
                      @for (f of manifest; track f) { <option [value]="f">{{ f }}</option> }
                    </select>
                    <label class="btn btn-outline-secondary btn-sm mb-0" title="Eigenes SVG hochladen">
                      <i class="bi bi-upload"></i>
                      <input type="file" accept="image/svg+xml,.svg" hidden
                             (change)="onUpload(row.item, $event)">
                    </label>
                    @if (row.item.svgData) {
                      <span class="badge bg-info" title="Eigenes hochgeladenes Symbol">eigen</span>
                      <button type="button" class="btn btn-outline-danger btn-sm"
                              (click)="clearSvgData(row.item)" title="Hochgeladenes Symbol entfernen">
                        <i class="bi bi-x-lg"></i>
                      </button>
                    }
                  }
                </div>
              </td>
              @if (!readonly) {
                <td class="text-end">
                  <button type="button" class="btn btn-outline-danger btn-sm"
                          (click)="deleteRow(row.index)" title="Punktart löschen">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              }
            </tr>
          } @empty {
            <tr><td [attr.colspan]="readonly ? 4 : 5" class="text-muted text-center py-3">Keine Einträge</td></tr>
          }
        </tbody>
      </table>
    </div>
    <datalist id="pa-kategorien">
      @for (k of kategorien; track k) { <option [value]="k"></option> }
    </datalist>
  `,
})
export class PunktartenEditorComponent implements OnChanges {
  /** Das zu editierende Array (wird IN PLACE mutiert). */
  @Input() data: any[] = [];
  /** Nur-Lese-Modus (keine Bearbeitung). */
  @Input() readonly = false;
  /** Feuert nach jeder Änderung. */
  @Output() changed = new EventEmitter<void>();

  /** Mitgelieferte Symbol-Dateinamen (generiert via tools/build-punktarten.mjs). */
  readonly manifest: string[] = POINTS_MANIFEST as string[];

  searchTerm = '';
  filteredCount = 0;
  view: { item: any; index: number }[] = [];
  kategorien: string[] = [];
  /** Mehrfach vergebene Nummern (fuer is-invalid + Warn-Banner). */
  duplicates: number[] = [];
  /** Beim Binden erfasste „bestehende" Zeilen — ihre Nummer ist gesperrt. */
  private existingRows = new Set<any>();

  ngOnChanges(changes?: SimpleChanges): void {
    // Neu (oder neu geladene) Liste gebunden: alle aktuell vorhandenen Zeilen
    // gelten als „bestehend" → ihre Nummer (= stabiler bp.type-Schluessel) ist
    // gesperrt. Nur in dieser Sitzung neu angelegte Zeilen duerfen die Nummer
    // setzen. (Im Test wird ngOnChanges ohne Argument aufgerufen.)
    if (!changes || changes['data']) {
      this.existingRows = new Set(this.data ?? []);
    }
    this.recompute();
  }

  recompute(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const all = this.data.map((item, index) => ({ item, index }));
    const filtered = term
      ? all.filter(r => `${r.item?.nummer ?? ''} ${r.item?.beschreibung ?? ''} ${r.item?.kategorie ?? ''}`.toLowerCase().includes(term))
      : all;
    this.filteredCount = filtered.length;
    this.view = filtered;
    this.kategorien = [...new Set(this.data.map(r => (r?.kategorie ?? '').trim()).filter(Boolean))].sort();
    this.duplicates = duplicatePunktartNummern(this.data);
  }

  /** Nummer ist nur fuer in dieser Sitzung neu angelegte Zeilen editierbar; die
   *  Nummer bestehender Eintraege ist der feste bp.type-Schluessel (gesperrt). */
  canEditNummer(row: any): boolean {
    return !this.readonly && !this.existingRows.has(row);
  }

  /** True, wenn die Nummer dieser Zeile mehrfach in der Liste vorkommt. */
  isDuplicateNummer(row: any): boolean {
    const n = Number(row?.nummer);
    return Number.isFinite(n) && this.duplicates.includes(n);
  }

  /** Aufgeloeste Vorschau-URL: hochgeladenes svgData hat Vorrang, sonst Asset. */
  iconUrl(item: any): string {
    if (item?.svgData) return item.svgData;
    return item?.svg ? `assets/img/points/${item.svg}` : 'assets/img/points/placeholder.svg';
  }

  setNummer(item: any, value: any): void {
    // Sperre: die Nummer bestehender Eintraege ist unveraenderlich (bestehende
    // Punkte verweisen ueber bp.type darauf — Aendern wuerde sie verwaisen).
    if (!this.canEditNummer(item)) return;
    item.nummer = value === '' || value === null ? null : Number(value);
    this.changed.emit();
    this.recompute(); // Dubletten neu bewerten
  }

  setField(item: any, field: 'beschreibung' | 'kategorie', value: string): void {
    item[field] = value;
    this.changed.emit();
  }

  /** Mitgeliefertes Symbol gewaehlt: svg setzen, evtl. Upload verwerfen. */
  selectSvg(item: any, file: string): void {
    item.svg = file || '';
    if (file) delete item.svgData;
    this.changed.emit();
  }

  onUpload(item: any, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      if (result.startsWith('data:image/svg')) {
        item.svgData = result;
        this.changed.emit();
        this.recompute();
      }
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  clearSvgData(item: any): void {
    delete item.svgData;
    this.changed.emit();
  }

  addRow(): void {
    const maxNr = this.data.reduce((m, r) => Math.max(m, Number(r?.nummer) || 0), 0);
    this.data.unshift({ nummer: maxNr + 1, beschreibung: '', svg: '', kategorie: '' });
    this.searchTerm = '';
    this.changed.emit();
    this.recompute();
  }

  deleteRow(index: number): void {
    this.data.splice(index, 1);
    this.changed.emit();
    this.recompute();
  }
}
